// src/services/readyMadeService.js - Updated with Cascade Delete
import {
  db,
  READY_MADE_INQUIRIES_COLLECTION,
  NOTIFICATIONS_COLLECTION,
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  getDoc,
  query,
  orderBy,
  where,
  limit,
  startAfter,
  serverTimestamp,
  arrayUnion,
  arrayRemove,
  writeBatch,
} from './firebase';

// API endpoint for notifications
const NOTIFY_API_URL = '/api/notify';

// Helper to call notification API
async function sendNotification(notificationData) {
  try {
    const response = await fetch(NOTIFY_API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(notificationData),
    });

    const result = await response.json();

    if (!result.success) {
      console.warn('Notification API returned error:', result.message);
      return { success: false, error: result.message };
    }

    console.log('✅ Notification sent successfully:', result);
    return result;
  } catch (error) {
    console.error('Failed to send notification:', error);
    return { success: false, error: error.message };
  }
}

// Validate email format
function isValidEmail(email) {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

export const readyMadeService = {
  // ==================== SUBMIT INQUIRY ====================
  async submitInquiry(inquiryData, packageData) {
    try {
      if (!isValidEmail(inquiryData.email)) {
        throw new Error('Invalid email address');
      }

      const inquiry = {
        ...inquiryData,
        packageData: {
          id: packageData.id,
          name: packageData.name,
          price: packageData.price,
          duration: packageData.duration,
          features: packageData.features || [],
          gradient: packageData.gradient || 'from-purple-500 to-pink-500',
          imageUrl: packageData.imageUrl || null,
          category: packageData.category || 'others',
        },
        inquiryType: 'ready-made',
        status: 'pending',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        isRead: false,
        isArchived: false,
        submittedAt: new Date().toISOString(),
        emailResponseSent: false,
        emailResponseSentAt: null,
        emailResponse: null,
        notes: [], // Array of note objects
        notificationIds: [], // Track associated notification IDs for cascade delete
      };

      const docRef = await addDoc(
        collection(db, READY_MADE_INQUIRIES_COLLECTION),
        inquiry,
      );

      // Send notification with email
      const notificationResult = await sendNotification({
        type: 'ready-made',
        inquiryData: {
          name: inquiryData.name,
          email: inquiryData.email,
          company: inquiryData.company || '',
          message: inquiryData.message || '',
          packageName: packageData.name,
          packagePrice: packageData.price,
          packageDuration: packageData.duration,
          packageFeatures: packageData.features || [],
          packageImage: packageData.imageUrl || null,
          serviceId: packageData.id,
        },
        adminUserId: null,
        sendEmail: true,
        useAdminTemplate: true,
      });

      // If notification was created, save the notification ID
      if (notificationResult?.data?.notificationId) {
        await updateDoc(docRef, {
          notificationIds: arrayUnion(notificationResult.data.notificationId),
        });
      }

      return {
        id: docRef.id,
        ...inquiry,
        notificationSent: notificationResult?.success || false,
        emailSent: notificationResult?.data?.emailSent || false,
        notificationId: notificationResult?.data?.notificationId || null,
      };
    } catch (error) {
      console.error('Error submitting ready-made inquiry:', error);
      throw error;
    }
  },

  // ==================== GET INQUIRIES ====================
  async getInquiries(filters = {}, lastDoc = null, limitCount = 20) {
    try {
      let q = query(
        collection(db, READY_MADE_INQUIRIES_COLLECTION),
        orderBy('createdAt', 'desc'),
        limit(limitCount),
      );

      if (filters.status && filters.status !== 'all') {
        q = query(q, where('status', '==', filters.status));
      }

      if (filters.isArchived !== undefined) {
        q = query(q, where('isArchived', '==', filters.isArchived));
      }

      if (lastDoc) {
        q = query(q, startAfter(lastDoc));
      }

      const snapshot = await getDocs(q);
      const inquiries = [];
      let lastVisible = null;

      snapshot.forEach(doc => {
        inquiries.push({ id: doc.id, ...doc.data() });
        lastVisible = doc;
      });

      return {
        inquiries,
        lastVisible,
        hasMore: snapshot.docs.length === limitCount,
      };
    } catch (error) {
      console.error('Error getting inquiries:', error);
      throw error;
    }
  },

  // ==================== GET SINGLE INQUIRY ====================
  async getInquiry(id) {
    try {
      const docRef = doc(db, READY_MADE_INQUIRIES_COLLECTION, id);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      }
      return null;
    } catch (error) {
      console.error('Error getting inquiry:', error);
      throw error;
    }
  },

  // ==================== UPDATE STATUS ====================
  async updateStatus(id, status, adminId = null, notes = null) {
    try {
      const updateData = { status, updatedAt: serverTimestamp() };

      if (notes) {
        updateData.notes = arrayUnion({
          id: `note_${Date.now()}`,
          text: notes,
          addedBy: adminId,
          addedByName: 'Admin',
          addedAt: new Date().toISOString(),
          isEdited: false,
        });
      }

      if (status === 'in-review') {
        updateData.reviewStartedAt = serverTimestamp();
      }

      if (status === 'approved' || status === 'declined') {
        updateData.decisionMadeAt = serverTimestamp();
        updateData.decidedBy = adminId;
      }

      if (status === 'completed') {
        updateData.completedAt = serverTimestamp();
      }

      const docRef = doc(db, READY_MADE_INQUIRIES_COLLECTION, id);
      await updateDoc(docRef, updateData);
      return true;
    } catch (error) {
      console.error('Error updating inquiry status:', error);
      throw error;
    }
  },

  // ==================== ADD RESPONSE ====================
  async addResponse(id, response, adminId, adminName) {
    try {
      const docRef = doc(db, READY_MADE_INQUIRIES_COLLECTION, id);
      const docSnap = await getDoc(docRef);
      const existingResponses = docSnap.data()?.responses || [];

      const newResponse = {
        message: response,
        sentBy: adminId,
        sentByName: adminName,
        sentAt: new Date().toISOString(),
        isRead: false,
      };

      await updateDoc(docRef, {
        responses: [...existingResponses, newResponse],
        status: 'responded',
        updatedAt: serverTimestamp(),
      });

      return true;
    } catch (error) {
      console.error('Error adding response:', error);
      throw error;
    }
  },

  // ==================== NOTE SYSTEM ====================
  async addNote(id, note, adminId) {
    try {
      const docRef = doc(db, READY_MADE_INQUIRIES_COLLECTION, id);
      const docSnap = await getDoc(docRef);
      const existingNotes = docSnap.data()?.notes || [];

      const newNote = {
        ...note,
        addedBy: adminId,
        addedAt: new Date().toISOString(),
        isEdited: false,
      };

      await updateDoc(docRef, {
        notes: [...existingNotes, newNote],
        updatedAt: serverTimestamp(),
      });

      return newNote;
    } catch (error) {
      console.error('Error adding note:', error);
      throw error;
    }
  },

  async updateNote(id, noteId, updatedNote, adminId) {
    try {
      const docRef = doc(db, READY_MADE_INQUIRIES_COLLECTION, id);
      const docSnap = await getDoc(docRef);
      const existingNotes = docSnap.data()?.notes || [];

      const updatedNotes = existingNotes.map(note =>
        note.id === noteId
          ? {
              ...updatedNote,
              editedBy: adminId,
              editedAt: new Date().toISOString(),
            }
          : note,
      );

      await updateDoc(docRef, {
        notes: updatedNotes,
        updatedAt: serverTimestamp(),
      });

      return true;
    } catch (error) {
      console.error('Error updating note:', error);
      throw error;
    }
  },

  async deleteNote(id, noteId, adminId) {
    try {
      const docRef = doc(db, READY_MADE_INQUIRIES_COLLECTION, id);
      const docSnap = await getDoc(docRef);
      const existingNotes = docSnap.data()?.notes || [];

      const updatedNotes = existingNotes.filter(note => note.id !== noteId);

      await updateDoc(docRef, {
        notes: updatedNotes,
        updatedAt: serverTimestamp(),
      });

      return true;
    } catch (error) {
      console.error('Error deleting note:', error);
      throw error;
    }
  },

  // ==================== UPDATE READY-MADE INQUIRY WITH EMAIL RESPONSE ====================
  async updateReadyMadeInquiryWithEmailResponse(
    id,
    response,
    adminId,
    adminName,
  ) {
    try {
      const docRef = doc(db, READY_MADE_INQUIRIES_COLLECTION, id);
      const docSnap = await getDoc(docRef);
      const existingResponses = docSnap.data()?.responses || [];

      const newResponse = {
        message: response,
        sentBy: adminId,
        sentByName: adminName || 'Admin',
        sentAt: new Date().toISOString(),
        isRead: false,
        isEmail: true,
      };

      await updateDoc(docRef, {
        responses: [...existingResponses, newResponse],
        status: 'responded',
        updatedAt: serverTimestamp(),
        emailResponseSent: true,
        emailResponseSentAt: serverTimestamp(),
        emailResponse: response,
      });

      return true;
    } catch (error) {
      console.error(
        'Error updating ready-made inquiry with email response:',
        error,
      );
      throw error;
    }
  },

  // ==================== CASCADE DELETE - Delete inquiry and all related data ====================
  async cascadeDeleteInquiry(id) {
    try {
      const docRef = doc(db, READY_MADE_INQUIRIES_COLLECTION, id);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        throw new Error('Inquiry not found');
      }

      const inquiryData = docSnap.data();
      const deletedItems = {
        inquiry: false,
        notes: 0,
        responses: 0,
        notifications: 0,
        notificationsDeleted: [],
        inquiryId: id,
      };

      console.log(`🗑️ Starting cascade delete for inquiry: ${id}`);

      // 1. Delete all notifications associated with this inquiry
      const notificationIds = inquiryData.notificationIds || [];

      if (notificationIds.length > 0) {
        console.log(
          `📧 Found ${notificationIds.length} notification(s) to delete`,
        );
        const notificationBatch = writeBatch(db);
        let notificationCount = 0;

        for (const notificationId of notificationIds) {
          try {
            const notifRef = doc(db, NOTIFICATIONS_COLLECTION, notificationId);
            notificationBatch.delete(notifRef);
            notificationCount++;
            deletedItems.notificationsDeleted.push(notificationId);
            console.log(`✅ Queued notification deletion: ${notificationId}`);
          } catch (error) {
            console.error(
              `❌ Error queuing notification ${notificationId}:`,
              error,
            );
          }
        }

        if (notificationCount > 0) {
          await notificationBatch.commit();
          deletedItems.notifications = notificationCount;
          console.log(`✅ Deleted ${notificationCount} notifications`);
        }
      } else {
        console.log('ℹ️ No notifications found to delete');
      }

      // 2. Also try to find notifications by data.inquiryId (fallback for older entries)
      try {
        const notificationsQuery = query(
          collection(db, NOTIFICATIONS_COLLECTION),
          where('data.inquiryId', '==', id),
        );
        const notificationsSnapshot = await getDocs(notificationsQuery);

        if (!notificationsSnapshot.empty) {
          const fallbackBatch = writeBatch(db);
          let fallbackCount = 0;

          notificationsSnapshot.forEach(doc => {
            fallbackBatch.delete(doc.ref);
            fallbackCount++;
            deletedItems.notificationsDeleted.push(doc.id);
            console.log(`✅ Found and queued fallback notification: ${doc.id}`);
          });

          await fallbackBatch.commit();
          deletedItems.notifications += fallbackCount;
          console.log(`✅ Deleted ${fallbackCount} fallback notifications`);
        }
      } catch (error) {
        console.error('❌ Error deleting fallback notifications:', error);
      }

      // 3. Also try to find notifications with package name reference (fallback)
      try {
        if (inquiryData.packageData?.name) {
          const packageNotificationsQuery = query(
            collection(db, NOTIFICATIONS_COLLECTION),
            where('data.packageName', '==', inquiryData.packageData.name),
          );
          const packageNotificationsSnapshot = await getDocs(
            packageNotificationsQuery,
          );

          if (!packageNotificationsSnapshot.empty) {
            const packageBatch = writeBatch(db);
            let packageCount = 0;

            packageNotificationsSnapshot.forEach(doc => {
              // Skip if already deleted
              if (!deletedItems.notificationsDeleted.includes(doc.id)) {
                packageBatch.delete(doc.ref);
                packageCount++;
                deletedItems.notificationsDeleted.push(doc.id);
                console.log(
                  `✅ Found and queued package notification: ${doc.id}`,
                );
              }
            });

            if (packageCount > 0) {
              await packageBatch.commit();
              deletedItems.notifications += packageCount;
              console.log(`✅ Deleted ${packageCount} package notifications`);
            }
          }
        }
      } catch (error) {
        console.error('❌ Error deleting package notifications:', error);
      }

      // 4. Notes and responses are embedded in the inquiry document, so they'll be deleted when the inquiry is deleted
      deletedItems.notes = inquiryData.notes?.length || 0;
      deletedItems.responses = inquiryData.responses?.length || 0;

      // 5. Delete the inquiry document itself
      await deleteDoc(docRef);
      deletedItems.inquiry = true;
      console.log(`✅ Inquiry document deleted: ${id}`);

      console.log(
        `✅ Cascade delete complete for inquiry ${id}:`,
        deletedItems,
      );
      return deletedItems;
    } catch (error) {
      console.error('Error in cascade delete:', error);
      throw error;
    }
  },

  // ==================== DELETE INQUIRY (with cascade) ====================
  async deleteInquiry(id) {
    return await this.cascadeDeleteInquiry(id);
  },

  // ==================== TOGGLE ARCHIVE ====================
  async toggleArchive(id, isArchived) {
    try {
      const docRef = doc(db, READY_MADE_INQUIRIES_COLLECTION, id);
      await updateDoc(docRef, { isArchived, updatedAt: serverTimestamp() });
      return true;
    } catch (error) {
      console.error('Error toggling archive:', error);
      throw error;
    }
  },

  // ==================== MARK AS READ ====================
  async markAsRead(id, adminId) {
    try {
      const docRef = doc(db, READY_MADE_INQUIRIES_COLLECTION, id);
      await updateDoc(docRef, {
        isRead: true,
        readAt: serverTimestamp(),
        readBy: adminId,
        updatedAt: serverTimestamp(),
      });
      return true;
    } catch (error) {
      console.error('Error marking as read:', error);
      throw error;
    }
  },

  // ==================== BULK ACTION (with cascade delete) ====================
  async bulkAction(ids, action, value = null) {
    if (!ids || ids.length === 0) return true;

    try {
      // For delete action, use cascade delete for each item
      if (action === 'delete') {
        let deletedCount = 0;
        let failedCount = 0;
        const results = [];

        for (const id of ids) {
          try {
            const result = await this.cascadeDeleteInquiry(id);
            deletedCount++;
            results.push({ id, success: true, ...result });
            console.log(`✅ Deleted inquiry ${id} with cascade`);
          } catch (err) {
            failedCount++;
            results.push({ id, success: false, error: err.message });
            console.error(`❌ Failed to delete inquiry ${id}:`, err);
          }
        }

        return {
          success: true,
          deletedCount,
          failedCount,
          results,
        };
      }

      // For other actions, use batch
      const batch = writeBatch(db);

      ids.forEach(id => {
        const docRef = doc(db, READY_MADE_INQUIRIES_COLLECTION, id);

        switch (action) {
          case 'archive':
            batch.update(docRef, {
              isArchived: true,
              updatedAt: serverTimestamp(),
            });
            break;
          case 'unarchive':
            batch.update(docRef, {
              isArchived: false,
              updatedAt: serverTimestamp(),
            });
            break;
          case 'mark-read':
            batch.update(docRef, {
              isRead: true,
              readAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
            });
            break;
          case 'mark-unread':
            batch.update(docRef, {
              isRead: false,
              updatedAt: serverTimestamp(),
            });
            break;
          case 'set-status':
            if (value) {
              const updateData = {
                status: value,
                updatedAt: serverTimestamp(),
              };
              if (value === 'in-review')
                updateData.reviewStartedAt = serverTimestamp();
              if (value === 'approved' || value === 'declined')
                updateData.decisionMadeAt = serverTimestamp();
              if (value === 'completed')
                updateData.completedAt = serverTimestamp();
              batch.update(docRef, updateData);
            }
            break;
          default:
            console.warn(`Unknown bulk action: ${action}`);
        }
      });

      await batch.commit();
      console.log(
        `✅ Bulk action "${action}" completed for ${ids.length} items`,
      );
      return { success: true, count: ids.length };
    } catch (error) {
      console.error('Error performing bulk action:', error);
      throw error;
    }
  },

  // ==================== GET STATS ====================
  async getStats() {
    try {
      const snapshot = await getDocs(
        collection(db, READY_MADE_INQUIRIES_COLLECTION),
      );
      let pending = 0,
        inReview = 0,
        responded = 0,
        approved = 0,
        declined = 0,
        completed = 0,
        archived = 0;

      snapshot.forEach(doc => {
        const data = doc.data();
        if (data.isArchived) archived++;
        else if (data.status === 'pending') pending++;
        else if (data.status === 'in-review') inReview++;
        else if (data.status === 'responded') responded++;
        else if (data.status === 'approved') approved++;
        else if (data.status === 'declined') declined++;
        else if (data.status === 'completed') completed++;
      });

      return {
        total: snapshot.size,
        pending,
        inReview,
        responded,
        approved,
        declined,
        completed,
        archived,
      };
    } catch (error) {
      console.error('Error getting stats:', error);
      return {
        total: 0,
        pending: 0,
        inReview: 0,
        responded: 0,
        approved: 0,
        declined: 0,
        completed: 0,
        archived: 0,
      };
    }
  },

  // ==================== GET PACKAGE DISTRIBUTION ====================
  async getPackageDistribution() {
    try {
      const snapshot = await getDocs(
        collection(db, READY_MADE_INQUIRIES_COLLECTION),
      );
      const distribution = {};

      snapshot.forEach(doc => {
        const packageName = doc.data().packageData?.name || 'Unknown Package';
        distribution[packageName] = (distribution[packageName] || 0) + 1;
      });

      return distribution;
    } catch (error) {
      console.error('Error getting package distribution:', error);
      return {};
    }
  },

  // ==================== GET TIMELINE DATA ====================
  async getTimelineData(days = 30) {
    try {
      const snapshot = await getDocs(
        collection(db, READY_MADE_INQUIRIES_COLLECTION),
      );
      const timeline = {};
      const now = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);

      for (let i = days; i >= 0; i--) {
        const date = new Date(now);
        date.setDate(date.getDate() - i);
        const dateKey = date.toISOString().split('T')[0];
        timeline[dateKey] = 0;
      }

      snapshot.forEach(doc => {
        const createdAt = doc.data().createdAt?.toDate();
        if (createdAt && createdAt >= startDate) {
          const dateKey = createdAt.toISOString().split('T')[0];
          if (timeline[dateKey] !== undefined) timeline[dateKey]++;
        }
      });

      return Object.entries(timeline).map(([date, count]) => ({ date, count }));
    } catch (error) {
      console.error('Error getting timeline data:', error);
      return [];
    }
  },
};

export default readyMadeService;
